import { useState, useEffect, useCallback } from 'react';
import { ModalOverlay } from '../components/UI';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

function authHeaders() {
  return { 'Content-Type': 'application/json' };
}

function UserModal({ user, onSave, onClose }) {
  const isEdit = !!user;
  const [form, setForm] = useState({
    username: user?.username || '',
    email:    user?.email    || '',
    role:     user?.role     || 'admin',
    password: '',
  });
  const [error, setError] = useState('');

  function setField(key, val) { setForm(f => ({ ...f, [key]: val })); }

  async function handleSave() {
    if (!form.username.trim() || !form.email.trim()) {
      setError('Username ed email sono obbligatori');
      return;
    }
    if (!isEdit && !form.password) {
      setError('Password obbligatoria per i nuovi utenti');
      return;
    }
    await onSave(form);
    onClose();
  }

  return (
    <ModalOverlay onClose={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--bg-2)', border: '1px solid var(--border)',
        borderRadius: 14, padding: 28, width: 'min(440px, 100%)',
        boxShadow: '0 24px 80px rgba(0,0,0,0.5)',
      }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 700, marginBottom: 20 }}>
          {isEdit ? 'Modifica utente' : 'Nuovo utente'}
        </div>

        {[
          { key: 'username', label: 'USERNAME',    type: 'text' },
          { key: 'email',    label: 'EMAIL',        type: 'email' },
          { key: 'password', label: isEdit ? 'NUOVA PASSWORD (vuoto = invariata)' : 'PASSWORD', type: 'password' },
        ].map(({ key, label, type }) => (
          <div key={key} style={{ marginBottom: 14 }}>
            <label style={{
              display: 'block', fontSize: 10, fontWeight: 600,
              color: 'var(--text-3)', letterSpacing: '0.12em',
              textTransform: 'uppercase', marginBottom: 6,
            }}>
              {label}
            </label>
            <input
              type={type}
              value={form[key]}
              onChange={e => setField(key, e.target.value)}
              style={inputStyle}
            />
          </div>
        ))}

        <div style={{ marginBottom: 20 }}>
          <label style={{
            display: 'block', fontSize: 10, fontWeight: 600,
            color: 'var(--text-3)', letterSpacing: '0.12em',
            textTransform: 'uppercase', marginBottom: 6,
          }}>
            RUOLO
          </label>
          <select
            value={form.role}
            onChange={e => setField('role', e.target.value)}
            style={inputStyle}
          >
            <option value="admin">admin</option>
            <option value="super_admin">super_admin</option>
          </select>
        </div>

        {error && (
          <div style={{ fontSize: 11, color: 'var(--red)', marginBottom: 12 }}>{error}</div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={btnStyle}>Annulla</button>
          <button
            onClick={handleSave}
            style={{ ...btnStyle, background: 'var(--accent)', color: '#fff', border: 'none' }}
          >
            {isEdit ? 'Salva' : 'Crea'}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

export default function Users() {
  const [users, setUsers]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [modalOpen, setModalOpen]     = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API_BASE}/users`, { headers: authHeaders(), credentials: 'include' });
      const data = await res.json();
      setUsers(data.users || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  async function handleSave(form) {
    try {
      if (editingUser) {
        const body = { username: form.username, email: form.email, role: form.role };
        if (form.password) body.password = form.password;
        const res = await fetch(`${API_BASE}/users/${editingUser.id}`, {
          method: 'PUT', headers: authHeaders(), credentials: 'include', body: JSON.stringify(body),
        });
        if (!res.ok) { alert('Errore durante il salvataggio'); return; }
      } else {
        const res = await fetch(`${API_BASE}/users`, {
          method: 'POST', headers: authHeaders(), credentials: 'include', body: JSON.stringify(form),
        });
        if (!res.ok) { alert('Errore durante la creazione'); return; }
      }
      setEditingUser(null);
      loadUsers();
    } catch (err) {
      console.error(err);
      alert('Errore di rete');
    }
  }

  async function handleDeactivate(id) {
    if (!window.confirm('Disattivare questo utente?')) return;
    try {
      const res = await fetch(`${API_BASE}/users/${id}`, { method: 'DELETE', headers: authHeaders(), credentials: 'include' });
      if (!res.ok) { alert('Errore durante la disattivazione'); return; }
      loadUsers();
    } catch (err) {
      console.error(err);
      alert('Errore di rete');
    }
  }

  return (
    <div className="page-content animate-fade">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700 }}>
          Gestione <span style={{ color: 'var(--accent)' }}>Utenti</span>
        </h1>
        <button
          onClick={() => { setEditingUser(null); setModalOpen(true); }}
          style={{ ...btnStyle, background: 'var(--accent)', color: '#fff', border: 'none' }}
        >
          + Nuovo utente
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-3)' }}>Caricamento…</div>
      ) : users.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-3)' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>◑</div>
          <div>Nessun utente trovato</div>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--font-mono)', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', color: 'var(--text-3)', textAlign: 'left' }}>
                {['Username', 'Email', 'Ruolo', 'Stato', 'Azioni'].map(h => (
                  <th key={h} style={{ padding: '8px 12px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', fontSize: 10 }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr
                  key={u.id}
                  style={{ borderBottom: '1px solid var(--border)', color: u.active ? 'var(--text-1)' : 'var(--text-3)' }}
                >
                  <td style={{ padding: '10px 12px' }}>{u.username}</td>
                  <td style={{ padding: '10px 12px' }}>{u.email}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <span style={{
                      padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                      background: u.role === 'super_admin' ? 'rgba(255,156,0,0.15)' : 'rgba(99,102,241,0.15)',
                      color:      u.role === 'super_admin' ? '#FF9C00' : '#6366f1',
                    }}>
                      {u.role}
                    </span>
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <span style={{ color: u.active ? 'var(--green)' : 'var(--red)', fontSize: 10 }}>
                      {u.active ? '● attivo' : '● inattivo'}
                    </span>
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        onClick={() => { setEditingUser(u); setModalOpen(true); }}
                        style={btnStyle}
                      >
                        ✎
                      </button>
                      {!!u.active && (
                        <button
                          onClick={() => handleDeactivate(u.id)}
                          style={{ ...btnStyle, color: 'var(--red)', borderColor: 'var(--red)' }}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalOpen && (
        <UserModal
          user={editingUser}
          onSave={handleSave}
          onClose={() => { setModalOpen(false); setEditingUser(null); }}
        />
      )}
    </div>
  );
}

const btnStyle = {
  padding: '6px 12px', borderRadius: 8, border: '1px solid var(--border)',
  background: 'var(--bg-3)', color: 'var(--text-1)', fontFamily: 'var(--font-mono)',
  fontSize: 11, cursor: 'pointer',
};

const inputStyle = {
  width: '100%', background: 'var(--bg-3)', border: '1px solid var(--border)',
  borderRadius: 8, padding: '9px 12px', color: 'var(--text-1)',
  fontFamily: 'var(--font-mono)', fontSize: 12, outline: 'none',
  boxSizing: 'border-box',
};
